import React, { useEffect, useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import IconButton from '@mui/material/IconButton'
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import RefreshIcon from '@mui/icons-material/Refresh'
import PauseIcon from '@mui/icons-material/Pause'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import { Link, router } from '@inertiajs/react'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface AgentStatus {
  agent: {
    id: number
    username: string
    slug: string
    agent_type: string
  }
  status: 'online' | 'idle' | 'error' | 'offline'
  last_heartbeat_at: string | null
  current_task: { id: number; summary: string; board_slug: string } | null
  heartbeat_metadata: Record<string, unknown>
  latest_session_log: {
    id: number
    session_id: string
    status: string
    started_at: string
  } | null
  error_count_24h: number
  recent_error_types: Record<string, number>
}

interface Props {
  agent_statuses: AgentStatus[]
}

const STATUS_CONFIG: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
  online: { color: 'success', label: 'Online' },
  idle: { color: 'warning', label: 'Idle' },
  error: { color: 'error', label: 'Error' },
  offline: { color: 'default', label: 'Offline' },
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSecs = Math.floor(diffMs / 1000)

  if (diffSecs < 60) return `${diffSecs}s ago`
  const diffMins = Math.floor(diffSecs / 60)
  if (diffMins < 60) return `${diffMins}m ago`
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) return `${diffHours}h ago`
  const diffDays = Math.floor(diffHours / 24)
  return `${diffDays}d ago`
}

const REFRESH_INTERVAL = 30_000

export default function Index({ agent_statuses }: Props) {
  const [autoRefresh, setAutoRefresh] = useState(true)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        router.reload({ only: ['agent_statuses'], preserveState: true })
      }, REFRESH_INTERVAL)
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [autoRefresh])

  function handleManualRefresh() {
    router.reload({ only: ['agent_statuses'], preserveState: true })
  }

  return (
    <SidebarLayout
      section="monitoring"
      title="Agent Status"
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Refresh now">
            <IconButton size="small" onClick={handleManualRefresh}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={autoRefresh ? 'Pause auto-refresh' : 'Resume auto-refresh (30s)'}>
            <IconButton size="small" onClick={() => setAutoRefresh(!autoRefresh)}>
              {autoRefresh ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          {autoRefresh && (
            <Typography variant="caption" color="text.secondary">
              Auto-refresh: 30s
            </Typography>
          )}
        </Box>
      }
    >
      <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 700 }}>
          <TableHead>
            <TableRow>
              <TableCell>Agent</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Last Heartbeat</TableCell>
              <TableCell>Current Task</TableCell>
              <TableCell>Session</TableCell>
              <TableCell align="right">Errors (24h)</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agent_statuses.map((as) => {
              const config = STATUS_CONFIG[as.status] || STATUS_CONFIG.offline
              return (
                <TableRow
                  key={as.agent.id}
                  hover
                  component={Link}
                  href={`/monitoring/agent_statuses/${as.agent.slug}`}
                  sx={{
                    textDecoration: 'none',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      @{as.agent.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        as.status === 'online' ? (
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 10,
                              '@keyframes pulse': {
                                '0%, 100%': { opacity: 1 },
                                '50%': { opacity: 0.3 },
                              },
                              animation: 'pulse 1.5s ease-in-out infinite',
                            }}
                          />
                        ) : undefined
                      }
                      label={config.label}
                      size="small"
                      color={config.color}
                      variant="outlined"
                      sx={{ height: 22, fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {as.agent.agent_type === 'machine' ? 'AI' : 'Human'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {as.last_heartbeat_at ? formatTimeAgo(as.last_heartbeat_at) : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {as.current_task ? (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 250 }}>
                        {as.current_task.summary}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {as.latest_session_log ? (
                      as.latest_session_log.status === 'streaming' ? (
                        <Chip label="Live" size="small" color="success" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
                      ) : (
                        <Typography variant="body2" color="text.secondary" fontSize="0.8rem">
                          {as.latest_session_log.status}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" color="text.disabled">-</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {as.error_count_24h > 0 ? (
                      <Chip
                        label={as.error_count_24h}
                        size="small"
                        color="error"
                        variant="outlined"
                        sx={{ height: 22, fontSize: '0.75rem', minWidth: 32 }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.disabled">0</Typography>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
            {agent_statuses.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No agents found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </SidebarLayout>
  )
}
